import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  jsonLd?: object | object[];
}

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

export default function SEOHead({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  jsonLd,
}: SEOHeadProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (attrKey: string, attrVal: string, content: string) => {
      let el = document.querySelector(`meta[${attrKey}="${attrVal}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrKey, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('name', 'description', description);
    if (keywords) setMeta('name', 'keywords', keywords);

    const resolvedCanonical = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.rel = 'canonical';
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = resolvedCanonical;

    setMeta('property', 'og:title', ogTitle ?? title);
    setMeta('property', 'og:description', ogDescription ?? description);
    setMeta('property', 'og:url', resolvedCanonical);
    if (ogImage) setMeta('property', 'og:image', ogImage);

    setMeta('name', 'twitter:title', ogTitle ?? title);
    setMeta('name', 'twitter:description', ogDescription ?? description);
    if (ogImage) setMeta('name', 'twitter:image', ogImage);

    // JSON-LD
    const prevScripts = document.querySelectorAll('script[data-seo-jsonld]');
    prevScripts.forEach((s) => s.remove());

    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      schemas.forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-jsonld', 'true');
        script.text = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    return () => {
      document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, jsonLd]);

  return null;
}

export { SITE_URL };
